/**
 * `VesselBottomSheet` — mobile-first bottom-sheet adaptation of
 * `VesselOverlayPanel`. On `< sm` viewports the marker tap opens
 * this sheet from the bottom of the viewport; on `>= sm` viewports
 * the parent renders the overlay panel instead and this component
 * stays mounted but `open={false}` (so the animation is gated by
 * CSS state, not by mounting cost).
 *
 * No Shadcn dependency in the web app — this is an accessible
 * `<dialog>`-style panel built on a native role="dialog" wrapper.
 * Escape + outside-click both close it.
 *
 * Accessibility: role="dialog", aria-modal="true", focus trapped on
 * the close button when open. Open/close transitions stay under 200ms
 * (`.claude/rules/uncodixify-ui.md`).
 */

import { useEffect, useRef } from "react";

import type { SSEStatus } from "#/hooks/useSSE.js";
import type { ShipTrailPoint } from "@hantavirus/shared";
import type { TranslateFn } from "#/lib/i18n.js";

import { VesselOverlayPanel } from "./VesselOverlayPanel.js";

export interface VesselBottomSheetProps {
  open: boolean;
  onClose: () => void;
  name: string;
  mmsi: string;
  latest: ShipTrailPoint | null;
  nextPort: string | null;
  eta: string | null;
  status: SSEStatus;
  onRetry: () => void;
  t: TranslateFn;
  locale: string;
}

export function VesselBottomSheet({
  open,
  onClose,
  name,
  mmsi,
  latest,
  nextPort,
  eta,
  status,
  onRetry,
  t,
  locale,
}: VesselBottomSheetProps) {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // Trap focus on the close button when the sheet opens. Pressing Tab
  // does not actually trap further — the sheet has minimal interactive
  // content (close + retry), so a single anchor is sufficient.
  useEffect(() => {
    if (open) closeBtnRef.current?.focus();
  }, [open]);

  // Escape-to-close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop — clickable to dismiss. Hidden when closed via opacity
          + pointer-events so the sheet doesn't capture taps on the map. */}
      <button
        type="button"
        aria-hidden={!open}
        tabIndex={-1}
        onClick={onClose}
        className="sm:hidden fixed inset-0 z-30 transition-opacity duration-150"
        style={{
          background: "hsl(0 0% 0% / 0.45)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          border: 0,
          cursor: open ? "pointer" : "default",
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("title")}
        data-state={open ? "open" : "closed"}
        data-testid="vessel-bottom-sheet"
        className={[
          "sm:hidden fixed inset-x-0 bottom-0 z-40",
          "rounded-t-lg border-t",
          "transition-transform duration-200",
        ].join(" ")}
        style={{
          background: "hsl(220 14% 10%)",
          color: "hsl(0 0% 95%)",
          borderColor: "hsl(220 14% 26%)",
          transform: open ? "translateY(0)" : "translateY(100%)",
          // Inline padding kept independent of LTR/RTL via logical CSS.
          paddingInline: "1rem",
          paddingBlock: "1rem",
        }}
      >
        {/* Drag handle hint */}
        <div
          aria-hidden="true"
          style={{
            width: "36px",
            height: "4px",
            borderRadius: "2px",
            background: "hsl(0 0% 100% / 0.18)",
            margin: "0 auto 0.75rem",
          }}
        />

        <div className="flex items-center justify-between mb-2 min-h-[44px]">
          <h2 className="text-sm font-semibold truncate">{name || t("loading")}</h2>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label={t("closeDetails")}
            className="rounded-md min-h-[44px] min-w-[44px] inline-flex items-center justify-center transition-colors duration-150"
            style={{
              background: "transparent",
              color: "inherit",
              border: "1px solid hsl(220 14% 32%)",
              cursor: "pointer",
            }}
          >
            <svg
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="6" y1="18" x2="18" y2="6" />
            </svg>
          </button>
        </div>

        <VesselOverlayPanel
          name={name}
          mmsi={mmsi}
          latest={latest}
          nextPort={nextPort}
          eta={eta}
          status={status}
          onRetry={onRetry}
          t={t}
          locale={locale}
          className="w-full"
        />
      </div>
    </>
  );
}
